import React from 'react';
import { ExternalLink } from 'lucide-react';
import {
  ProposalBadge,
  ProposalButton,
  ProposalProvenance,
  compactList,
  displayValue,
  labelize,
  statusKind,
} from './ProposalItems';

export function initialAttributeDraft(attribute) {
  const valueType = attribute?.value_type;
  const proposed = attribute?.proposed_value;
  if (valueType === 'enum_set') return Array.isArray(proposed) ? [...proposed] : compactList(proposed);
  if (valueType === 'enum') return Array.isArray(proposed) ? (proposed[0] || '') : (proposed || '');
  if (valueType === 'bool') return Boolean(proposed);
  if (valueType === 'structured_payload' || valueType === 'range') {
    const structured = attribute?.proposed_value_json ?? proposed ?? {};
    return JSON.stringify(structured, null, 2);
  }
  if (valueType === 'count') return proposed ?? '';
  return proposed ?? attribute?.proposed_value_text ?? '';
}

export function parseAttributeDraftValue(attribute, draftValue) {
  const valueType = attribute?.value_type;
  if (valueType === 'enum_set') return Array.isArray(draftValue) ? draftValue : compactList(draftValue);
  if (valueType === 'enum') return String(draftValue || '').trim();
  if (valueType === 'bool') return Boolean(draftValue);
  if (valueType === 'structured_payload' || valueType === 'range') {
    if (typeof draftValue !== 'string') return draftValue;
    const text = draftValue.trim();
    return text ? JSON.parse(text) : {};
  }
  if (valueType === 'count') {
    const value = Number(draftValue);
    if (!Number.isInteger(value)) throw new Error('Count values must be whole numbers.');
    return value;
  }
  return String(draftValue || '').trim();
}

const ATTRIBUTE_REJECTION_REASONS = [
  ['wrong_value', 'Wrong value'],
  ['unsupported_by_evidence', 'Unsupported by evidence'],
  ['not_guest_relevant', 'Not guest-relevant'],
  ['wrong_kind', 'Wrong kind'],
  ['source_is_stale', 'Source is stale'],
  ['duplicate', 'Duplicate'],
  ['needs_better_source', 'Needs better source'],
];

function AttributeDraftCheckboxes({ attribute, draftValue, onDraftChange }) {
  const allowed = attribute.allowed_values || [];
  const selected = new Set(Array.isArray(draftValue) ? draftValue : []);
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {allowed.map(value => (
        <label key={value.value} className="flex items-center gap-2 rounded-md border border-teal-100 bg-white px-3 py-2 text-sm text-stone-700">
          <input
            type="checkbox"
            checked={selected.has(value.value)}
            onChange={event => {
              const next = new Set(selected);
              if (event.target.checked) next.add(value.value);
              else next.delete(value.value);
              onDraftChange?.(Array.from(next));
            }}
          />
          <span>{value.label || labelize(value.value)}</span>
        </label>
      ))}
    </div>
  );
}

function AttributeValueEditor({ attribute, draftValue, onDraftChange }) {
  const valueType = attribute.value_type;
  const allowed = attribute.allowed_values || [];
  if (valueType === 'enum_set') {
    return <AttributeDraftCheckboxes attribute={attribute} draftValue={draftValue} onDraftChange={onDraftChange} />;
  }
  if (valueType === 'enum') {
    return (
      <select
        value={draftValue || ''}
        onChange={event => onDraftChange?.(event.target.value)}
        className="w-full rounded-lg border border-teal-200 bg-white px-3 py-2 text-sm text-stone-900 focus:border-teal-700 focus:outline-none focus:ring-1 focus:ring-teal-700"
      >
        <option value="">Choose value...</option>
        {allowed.map(value => <option key={value.value} value={value.value}>{value.label || labelize(value.value)}</option>)}
      </select>
    );
  }
  if (valueType === 'bool') {
    return (
      <select
        value={draftValue ? 'true' : 'false'}
        onChange={event => onDraftChange?.(event.target.value === 'true')}
        className="w-full rounded-lg border border-teal-200 bg-white px-3 py-2 text-sm text-stone-900 focus:border-teal-700 focus:outline-none focus:ring-1 focus:ring-teal-700"
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  return (
    <textarea
      rows={valueType === 'structured_payload' || valueType === 'range' ? 6 : 3}
      value={draftValue}
      onChange={event => onDraftChange?.(event.target.value)}
      className="w-full rounded-lg border border-teal-200 bg-white px-3 py-2 text-sm leading-6 text-stone-900 focus:border-teal-700 focus:outline-none focus:ring-1 focus:ring-teal-700"
    />
  );
}

export function AttributeProposalItem({
  attribute,
  editing = false,
  draftValue,
  rejectionReason = '',
  savingAction = null,
  onDraftChange,
  onStartEdit,
  onCancelEdit,
  onReview,
  onRejectionReasonChange,
}) {
  const status = attribute.status || 'proposed';
  const isPending = status === 'proposed';
  const consequence = attribute.consequence_class || 'descriptive';

  return (
    <div className="rounded-xl border border-teal-200 bg-white p-4 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <ProposalBadge kind={consequence === 'hard_constraint' ? 'danger' : (consequence === 'dietary' ? 'success' : 'accent')}>
          {labelize(consequence)}
        </ProposalBadge>
        <ProposalBadge kind="accent">{labelize(attribute.value_type)}</ProposalBadge>
        {attribute.card_key && <ProposalBadge>Card: {attribute.card_key}</ProposalBadge>}
        <p className="font-medium text-stone-900">{attribute.kind_label || attribute.target_label || labelize(attribute.kind)}</p>
        <ProposalBadge kind={statusKind(status)}>{labelize(status)}</ProposalBadge>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div>
          <p className="text-xs font-medium text-stone-400">Current approved</p>
          <p className="whitespace-pre-wrap break-words text-stone-500">{displayValue(attribute.current_value)}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-stone-400">Proposed</p>
          <p className="whitespace-pre-wrap break-words text-stone-900">{displayValue(attribute.proposed_value)}</p>
        </div>
      </div>

      {attribute.overrides_inherited && (
        <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50/70 p-3 text-xs text-stone-700">
          <p className="font-medium text-amber-900">Inherited place value</p>
          <p className="mt-1 break-words">{displayValue(attribute.inherited_value)}</p>
        </div>
      )}

      {(attribute.evidence_snippet || attribute.source_url) && (
        <div className="mt-3 rounded-lg bg-stone-50 p-3 text-xs text-stone-600">
          {attribute.evidence_snippet && <p>{attribute.evidence_snippet}</p>}
          {attribute.source_url && (
            <a href={attribute.source_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-blue-700">
              Attribute evidence <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}

      <ProposalProvenance provenance={attribute.provenance} />

      {isPending && (
        <div className="mt-4 flex flex-wrap gap-2">
          <ProposalButton kind="primary" onClick={() => onReview?.('approve')} disabled={Boolean(savingAction)} loading={savingAction === 'approve'}>
            Approve
          </ProposalButton>
          <ProposalButton kind="accent" onClick={onStartEdit} disabled={Boolean(savingAction)}>
            Edit proposal
          </ProposalButton>
        </div>
      )}

      {isPending && editing && (
        <div className="mt-3 rounded-lg border border-teal-200 bg-teal-50/40 p-3">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-teal-800">Edited value</span>
            <AttributeValueEditor attribute={attribute} draftValue={draftValue} onDraftChange={onDraftChange} />
          </label>
          {(attribute.value_type === 'structured_payload' || attribute.value_type === 'range') && (
            <p className="mt-1 text-xs text-teal-800">Structured edits must stay valid JSON.</p>
          )}
          {['enum', 'enum_set'].includes(attribute.value_type) && (
            <p className="mt-1 text-xs text-teal-800">Values are limited to the approved registry.</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <ProposalButton kind="accent" onClick={() => onReview?.('edit_and_approve')} disabled={Boolean(savingAction)} loading={savingAction === 'edit_and_approve'}>
              Apply edited
            </ProposalButton>
            <ProposalButton onClick={onCancelEdit} disabled={Boolean(savingAction)}>
              Cancel
            </ProposalButton>
          </div>
        </div>
      )}

      {isPending && (
        <div className="mt-4 rounded-lg border border-red-100 bg-red-50/50 p-3">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-red-800">Reject reason</span>
            <select
              value={rejectionReason}
              onChange={event => onRejectionReasonChange?.(event.target.value)}
              className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-stone-900 focus:border-red-600 focus:outline-none focus:ring-1 focus:ring-red-600"
            >
              <option value="">Choose reason...</option>
              {ATTRIBUTE_REJECTION_REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <div className="mt-3">
            <ProposalButton kind="danger" onClick={() => onReview?.('reject')} disabled={Boolean(savingAction) || !rejectionReason} loading={savingAction === 'reject'}>
              Reject
            </ProposalButton>
          </div>
        </div>
      )}
    </div>
  );
}
